import { useCallback, useEffect, useMemo, useState } from 'react';
import { Badge, EmptyState, PageHeader, Table, type TableColumn } from '../../components/shared';
// 쪽 머리 경로 · 범위 칩은 메뉴 배치에서(2026-10-17 · web 레인 W6 — lib/page-crumbs.ts)
import { crumbsFor, scopeFor } from '../../lib/page-crumbs';
import {
  listExpenseClaims, fetchExpenseSummary,
  type ExpenseClaimDto, type ExpenseAccountSummaryDto,
} from '../../api/expense';
import './ExpenseClaims.css';

/**
 * 지출 집행 — 「지출결의가 어떻게 됐나」 (2026-09-04 · 예산 판 B1).
 *
 * ## ★★★ 왜 이 화면이 판 B1 에 함께 있나
 *
 * **「표가 있다 ≠ 쓸 수 있다」** — 이 저장소가 되풀이해 겪은 것이다(보상휴가는 표도 칸도
 * 있었는데 **넣을 입구가 없어** 아무 일도 안 일어났다). 표만 만들고 화면을 다음 판으로
 * 미루면 그동안 쌓이는 행을 **아무도 못 본다.**
 *
 * ## ★★★ 주소를 가른다 — 화면이 「나는 누구냐」를 묻지 않는다
 *
 * 내 것(`/approval/expense`)과 회사 전체(`/approval/admin/expense`)는 **다른 주소**다.
 * 한 화면이 두 자격을 겸하면 서버에 「나는 관리자냐」를 찔러 물어야 하고, 그 물음은
 * 주소를 가르면 사라진다(2026-08-31 에 배운 것).
 *
 * ## ★★ 승인된 것만 «집행» 이다
 *
 * 신청중은 따로 센다. 둘을 합치면 「쓴 돈」이 부풀고, 예산에 붙이는 판 B3 에서
 * **같은 예산이 두 번 나가는** 사고가 된다(연차가 「잔여」와 「가용」을 이름부터 가른 이유).
 *
 * ## ★ 예산은 아직 없다
 *
 * 판 B2 가 예산 편성을 만들면 여기에 「예산 대비」가 붙는다. 지금은 **얼마를 썼나**까지다 —
 * 그것만으로도 지금까지 답할 수 없던 물음이다.
 */

/** 상태 알약 — 색만으로 뜻을 만들지 않는다(정본 P5). 알약 안에 낱말이 함께 적힌다. */
const STATUS: Record<string, { label: string; tone: 'success' | 'warning' | 'danger' | 'neutral' }> = {
  PENDING: { label: '결재중', tone: 'warning' },
  APPROVED: { label: '승인', tone: 'success' },
  REJECTED: { label: '반려', tone: 'danger' },
  CANCELED: { label: '회수', tone: 'neutral' },
};

const won = (v: string | number) => `${Number(v).toLocaleString()}원`;

export function ExpenseClaimsPage({ scope, title, description }: {
  scope: 'mine' | 'company';
  /**
   * 화면 제목 — **부르는 자리가 준다**(문서함 셋이 라우터에서 제목을 받는 것과 같다).
   * ★ 삼항으로 화면 안에서 고르면 「메뉴 이름 = 화면 제목」 검사가 글자를 못 찾는다 —
   *   그리고 그건 검사의 흠이 아니라, **제목이 자리마다 다르면 자리가 주는 것이 맞다**는 뜻이다.
   */
  title: string;
  description: string;
}) {
  const [claims, setClaims] = useState<ExpenseClaimDto[]>([]);
  const [accounts, setAccounts] = useState<ExpenseAccountSummaryDto[]>([]);
  const [totals, setTotals] = useState({ approved: 0, pending: 0 });
  const [state, setState] = useState<'loading' | 'ok' | 'folded'>('loading');
  const [openId, setOpenId] = useState<string | null>(null);

  const load = useCallback(async () => {
    setState('loading');
    try {
      const [list, sum] = await Promise.all([
        listExpenseClaims({ view: scope, limit: 50 }),
        fetchExpenseSummary({ view: scope }),
      ]);
      setClaims(list.claims);
      setAccounts(sum.accounts);
      setTotals({ approved: sum.totalApproved, pending: sum.totalPending });
      setState('ok');
    } catch {
      // ★ 옛 서버·권한 어느 쪽이든 그 절만 접는다 — 셸을 깨뜨리지 않는다.
      setState('folded');
    }
  }, [scope]);

  useEffect(() => { void load(); }, [load]);

  const claimColumns: ReadonlyArray<TableColumn<ExpenseClaimDto>> = useMemo(() => [
    {
      key: 'createdAt', header: '올린 날', width: '104px',
      render: (c) => c.createdAt.slice(0, 10),
    },
    ...(scope === 'company'
      ? [{ key: 'drafterName', header: '기안자', width: '110px' } as TableColumn<ExpenseClaimDto>]
      : []),
    {
      key: 'documentTitle', header: '문서',
      render: (c) => (
        <>
          {c.documentTitle ?? <span className="exp__dim">문서가 지워졌습니다</span>}
          <span className="exp__dim"> · {c.lines.length}줄</span>
        </>
      ),
    },
    {
      key: 'totalAmount', header: '금액', width: '128px', numeric: true,
      render: (c) => won(c.totalAmount),
    },
    {
      key: 'status', header: '상태', width: '92px',
      render: (c) => {
        const s = STATUS[c.status] ?? { label: c.status, tone: 'neutral' as const };
        return <Badge variant={s.tone === 'neutral' ? 'default' : s.tone} size="sm">{s.label}</Badge>;
      },
    },
  ], [scope]);

  const accountColumns: ReadonlyArray<TableColumn<ExpenseAccountSummaryDto>> = useMemo(() => [
    {
      key: 'label', header: '계정과목',
      render: (a) => (a.label ?? <span className="exp__dim">계정과목을 안 고른 줄</span>),
    },
    { key: 'lines', header: '줄', width: '64px', numeric: true },
    {
      key: 'approved', header: '집행 (승인)', width: '132px', numeric: true,
      render: (a) => won(a.approved),
    },
    {
      key: 'pending', header: '결재중', width: '120px', numeric: true,
      // ★★ 0 을 「없다」로 그리지 않는다 — 0원은 «셌는데 0» 이라 숫자로 적는다.
      render: (a) => <span className="exp__dim">{won(a.pending)}</span>,
    },
  ], []);

  return (
    /*
     * ★★★ **바깥 상자가 있어야 한다** (2026-09-04 · 사용자 지적 「디자인이 다 깨져 있다」).
     *    결재 셸의 `.aprnav__body` 는 **스크롤 통일 뿐 여백이 없다** — 화면마다 제 상자로
     *    여백·폭을 잡는 것이 이 셸의 규약이다(`.asts` · `.apl` · `.hra` 가 전부 그렇다).
     */
    <div className="exp">
      <PageHeader
        breadcrumb={crumbsFor(scope === 'company' ? '/approval/admin/expense' : '/approval/expense')}
        scope={scopeFor(scope === 'company' ? '/approval/admin/expense' : '/approval/expense')}
        title={title}
        description={description}
      />

      {state === 'loading' && <div className="exp__note">불러오는 중…</div>}
      {state === 'folded' && (
        <div className="exp__note">
          지출 집행을 아직 볼 수 없습니다 — 서버가 이 기능을 갖추면 여기에 나타납니다.
        </div>
      )}

      {state === 'ok' && (
        <>
          {/* ── 계정과목별 — 예산이 없어도 답할 수 있는 첫 물음 ── */}
          <section className="exp__panel">
            <h2 className="exp__panelt">
              계정과목별
              <span className="exp__panels">
                집행 {won(totals.approved)} · 결재중 {won(totals.pending)}
              </span>
            </h2>
            {/*
              ★★★ **둘을 더해 적지 않는다.** 「집행」은 승인된 것이고 「결재중」은 아직 아니다 —
                 한 숫자로 합치면 「쓴 돈」이 부풀고, 예산에 붙일 때 같은 돈이 두 번 나간다.
            */}
            <p className="exp__hint">
              집행은 <b>승인된 것</b>만 셉니다. 결재중은 아직 쓴 돈이 아니라 따로 적습니다.
            </p>
            <Table
              caption="계정과목별 지출"
              columns={accountColumns}
              rows={accounts}
              rowKey={(a) => a.code ?? '(없음)'}
              empty={(
                <EmptyState
                  title="아직 지출이 없습니다"
                  description="지출결의서를 올리면 계정과목별로 여기에 모입니다."
                />
              )}
            />
          </section>

          {/* ── 문서별 ── */}
          <section className="exp__panel">
            <h2 className="exp__panelt">
              지출결의
              <span className="exp__panels">{claims.length}건</span>
            </h2>
            <Table
              caption="지출결의 목록"
              columns={claimColumns}
              rows={claims}
              rowKey={(c) => c.id}
              onRowClick={(c) => setOpenId(openId === c.id ? null : c.id)}
              empty={(
                <EmptyState
                  title="올린 지출결의가 없습니다"
                  description="전자결재에서 «지출결의서» 양식으로 올리면 여기에 남습니다."
                />
              )}
            />

            {/* ★ 고른 문서의 내역 줄 — 표 안에 표를 넣지 않고 아래에 편다. */}
            {openId && (() => {
              const c = claims.find((x) => x.id === openId);
              if (!c) return null;
              return (
                <div className="exp__lines">
                  <h3 className="exp__linest">
                    {c.documentTitle ?? '지운 문서'}<span className="exp__dim"> · 내역 {c.lines.length}줄</span>
                  </h3>
                  <ul className="exp__linelist">
                    {c.lines.map((l) => (
                      <li key={l.id}>
                        <span className="exp__lday">{l.spentOn ?? '날짜 없음'}</span>
                        <span className="exp__lacc">{l.accountLabel ?? '계정과목 없음'}</span>
                        <span className="exp__ldesc">
                          {l.description ?? ''}
                          {/*
                            ★★ **어느 예산을 먹었나** (판 B3) — 못 붙은 줄은 그렇게 적는다.
                               「예산 없음」은 **잘못이 아니라 사실**이라 붉게 쓰지 않는다.
                          */}
                          <span className="exp__lbudget">
                            {l.budgetName ?? '예산 없음'}
                          </span>
                        </span>
                        <span className="exp__lamt">{won(l.amount)}</span>
                      </li>
                    ))}
                  </ul>
                </div>
              );
            })()}
          </section>
        </>
      )}
    </div>
  );
}

/**
 * 라우터가 부르는 두 벌 — **주소가 자격을 정한다**(화면이 「나는 누구냐」를 묻지 않는다).
 * ★ 제목은 메뉴 이름과 **글자 그대로** 같다(`lib/appr-menu.test.ts` 가 못 박는다).
 */
export function MyExpenseClaimsPage() {
  return (
    <ExpenseClaimsPage
      scope="mine"
      title="내 지출결의"
      description="내가 올린 지출결의가 어떻게 됐는지 봅니다."
    />
  );
}

export function CompanyExpenseClaimsPage() {
  return (
    <ExpenseClaimsPage
      scope="company"
      title="지출 집행"
      description="승인된 지출결의를 계정과목별로 모읍니다 — 실제 지급은 여기서 하지 않습니다."
    />
  );
}
